import { useEffect, useState } from 'react';
import { Tabs, Tab, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useSecurity } from '../Security/security';
import MaintenanceBannerDetails from '../MaintenanceBannerDetails';
import { getAppVersion, setAppVersion } from '../apiCalls';
import './AppSettings.css';

const VERSION_REGEX = /^\d+\.\d+\.\d+\.\d+$/;

const AppSettings = () => {
  const { validateRole } = useSecurity();
  const canEdit = validateRole({ department: 'IT' }) || validateRole({ isAdmin: true });

  const [activeTab, setActiveTab] = useState('version');
  const [currentVersion, setCurrentVersion] = useState('');
  const [inputVersion, setInputVersion] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        setLoading(true);
        const data = await getAppVersion();
        const v = data?.versionNumber || data?.[0]?.versionNumber || '';
        setCurrentVersion(v);
        setInputVersion(v);
      } catch (err) {
        setError('Unable to fetch current version.');
      } finally {
        setLoading(false);
      }
    };
    fetchVersion();
  }, []);

  const handleSave = async () => {
    setError('');
    setSuccess('');
    if (!VERSION_REGEX.test(inputVersion)) {
      setError('Version must match n.n.n.n (numbers only).');
      return;
    }
    try {
      setSaving(true);
      await setAppVersion(inputVersion);
      setCurrentVersion(inputVersion);
      setSuccess('Version updated.');
    } catch (err) {
      setError('Failed to save version.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container py-4 app-settings-page">
      <h3 className="mb-3 app-settings-title">App Settings</h3>
      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'version')} className="mb-3 app-settings-tabs">
        <Tab eventKey="version" title="Version">
          <div className="p-3 bg-white rounded shadow-sm">
            {loading ? (
              <div className="d-flex align-items-center gap-2">
                <Spinner animation="border" size="sm" /> Loading...
              </div>
            ) : (
              <>
                {error && (
                  <Alert variant="danger" className="py-2">
                    {error}
                  </Alert>
                )}
                {success && (
                  <Alert variant="success" className="py-2">
                    {success}
                  </Alert>
                )}
                <Form className="d-flex flex-column gap-3" style={{ maxWidth: '320px' }}>
                  <Form.Group>
                    <Form.Label>Current Version</Form.Label>
                    <Form.Control type="text" value={currentVersion || ''} readOnly />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Set Version (n.n.n.n)</Form.Label>
                    <Form.Control
                      type="text"
                      value={inputVersion || ''}
                      onChange={(e) => setInputVersion(e.target.value)}
                      placeholder="e.g. 2.0.2.1"
                      disabled={!canEdit}
                    />
                  </Form.Group>
                  <div>
                    <Button variant="primary" onClick={handleSave} disabled={!canEdit || saving}>
                      {saving ? 'Saving...' : 'Save'}
                    </Button>
                    {!canEdit && <div className="text-muted mt-2">Read-only (IT/Admin only).</div>}
                  </div>
                </Form>
              </>
            )}
          </div>
        </Tab>
        <Tab eventKey="maintenance" title="Maintenance Banner">
          <div className="p-3 bg-white rounded shadow-sm">
            <MaintenanceBannerDetails />
          </div>
        </Tab>
      </Tabs>
    </div>
  );
};

export default AppSettings;
